/* =========================================================================
   Character registry — registerCharacter / getCharacter / getLine.
   getLine() picks a line for a Moment with a session no-repeat picker (copied
   from nextDykFact, dykData.ts:28-35) and evaluates optional memory conditions
   against the GTKY cache, preferring a satisfied personalised line and falling
   back to a generic one. THIS IS THE v2 CMS SEAM — app.CharacterLines will
   hydrate over exactly this interface with zero client rework.
   ========================================================================= */
import type { CharacterDef, LineDef, Moment } from './types';
import { allMemory, GTKY_DECLINE_SENTINEL } from './memory';
import { resolvePlayerName, interpolateName, NAME_SKIP_SENTINEL } from './name';

const registry = new Map<string, CharacterDef>();
/** Per (characterId|moment) set of pool indices already shown this session. */
const shown = new Map<string, Set<number>>();

/** Ultimate safety net when a character or moment has no lines at all. */
const SAFE_GENERIC = 'Peace be with you.';

export function registerCharacter(def: CharacterDef): void {
  registry.set(def.id, def);
}

export function getCharacter(id: string): CharacterDef | undefined {
  return registry.get(id);
}

/** Reset the no-repeat memory (used by tests; harmless in app). */
export function resetLinePicker(): void {
  shown.clear();
}

function memorySatisfied(line: LineDef, memory: Record<string, string>): boolean {
  if (!line.requiresMemoryKey) return true;
  const val = memory[line.requiresMemoryKey];
  if (val == null) return false;
  // A sentinel occupies the key so the question is never re-asked — it is NOT an
  // answer. Personalising off "Prefer not to say" / a skipped name would be the
  // opposite of respecting the decline.
  if (val === GTKY_DECLINE_SENTINEL || val === NAME_SKIP_SENTINEL) return false;
  if (line.requiresMemoryValue != null) return val === line.requiresMemoryValue;
  return true;
}

function pickNoRepeat(key: string, pool: LineDef[]): LineDef {
  let seen = shown.get(key);
  if (!seen) {
    seen = new Set<number>();
    shown.set(key, seen);
  }
  if (seen.size >= pool.length) seen.clear();
  let i: number;
  do {
    i = Math.floor(Math.random() * pool.length);
  } while (seen.has(i));
  seen.add(i);
  return pool[i];
}

/**
 * Select a line for `moment`. Selection order:
 *   1. memory-conditioned lines whose condition is currently satisfied (personalised);
 *   2. otherwise, unconditioned lines (generic);
 *   3. `gallery:<key>` with no lines falls back to the `gallery-generic` set;
 *   4. finally the character's `generic` set, then a hard-coded safe line.
 * Any `{name}` token is filled from the player's name (authed displayName / guest
 * 'player_name'); when no name is known, lines carrying a `{name}` token are never
 * selected — a token-free variant is used instead, so the copy always reads cleanly.
 */
export function getLine(
  characterId: string,
  moment: Moment,
  memory: Record<string, string> = allMemory(),
  name: string = resolvePlayerName(),
): string {
  const def = registry.get(characterId);
  if (!def) return SAFE_GENERIC;

  let all = def.lines[moment];
  if ((!all || all.length === 0) && moment.startsWith('gallery:')) {
    all = def.lines['gallery-generic'];
  }
  if (!all || all.length === 0) {
    return def.lines['generic']?.[0]?.text ?? SAFE_GENERIC;
  }

  // When no name is known, {name} lines are ineligible (they'd read awkwardly).
  const nameOk = (l: LineDef): boolean => name !== '' || !l.text.includes('{name}');
  const conditioned = all.filter((l) => l.requiresMemoryKey && memorySatisfied(l, memory) && nameOk(l));
  const unconditioned = all.filter((l) => !l.requiresMemoryKey && nameOk(l));
  const pool = conditioned.length > 0 ? conditioned : unconditioned;
  const effective = pool.length > 0 ? pool : all.filter((l) => memorySatisfied(l, memory) && nameOk(l));
  if (effective.length === 0) {
    return def.lines['generic']?.[0]?.text ?? SAFE_GENERIC;
  }
  // Key the no-repeat set to the effective pool shape so personalised vs generic
  // pools don't cross-contaminate each other's cursor.
  const cursorKey = `${characterId}|${String(moment)}|${pool === conditioned ? 'c' : 'u'}`;
  return interpolateName(pickNoRepeat(cursorKey, effective).text, name);
}
